import React from 'react';
import PollOptionEditor from './PollOptionEditor';
import PollTypeSelector from './PollTypeSelector';
import {
  MAX_POLL_OPTIONS,
  MAX_POLL_TITLE_LENGTH,
  MIN_POLL_OPTIONS,
  createPollOption,
  validatePollOptionText,
} from './pollFormValidation';
import css from './CreatePollForm.module.css';

const CreatePollForm = props => {
  const {
    pollState,
    setPollState,
    disabled,
    latestOptionId,
    setLatestOptionId,
  } = props;

  const setOptionText = (localId, text) => {
    setPollState(prevState => ({
      ...prevState,
      formError: null,
      formNotice: null,
      options: prevState.options.map(option =>
        option.localId === localId ? { ...option, text, error: null } : option
      ),
    }));
  };

  const saveOption = localId => {
    setPollState(prevState => {
      const targetOption = prevState.options.find(option => option.localId === localId);
      const error = validatePollOptionText(targetOption?.text, prevState.options, localId);

      return {
        ...prevState,
        options: prevState.options.map(option =>
          option.localId === localId
            ? {
              ...option,
              text: String(option.text || '').trim(),
              isEditing: Boolean(error),
              error,
            }
            : option
        ),
      };
    });
  };

  const editOption = localId => {
    setLatestOptionId(localId);
    setPollState(prevState => ({
      ...prevState,
      options: prevState.options.map(option =>
        option.localId === localId ? { ...option, isEditing: true } : option
      ),
    }));
  };

  const removeOption = localId => {
    setPollState(prevState => {
      if (prevState.options.length <= MIN_POLL_OPTIONS) {
        return {
          ...prevState,
          formError: `Polls need at least ${MIN_POLL_OPTIONS} options.`,
          formNotice: null,
        };
      }

      const nextOptions = prevState.options.filter(option => option.localId !== localId);
      const nextFocusOption = nextOptions[nextOptions.length - 1];
      if (nextFocusOption) {
        setLatestOptionId(nextFocusOption.localId);
      }

      return {
        ...prevState,
        formError: null,
        formNotice: null,
        options: nextOptions.map(option => ({ ...option, error: null })),
      };
    });
  };

  const addOption = () => {
    setPollState(prevState => {
      if (prevState.options.length >= MAX_POLL_OPTIONS) {
        return {
          ...prevState,
          formNotice: `Polls can have at most ${MAX_POLL_OPTIONS} options.`,
        };
      }

      const nextOption = createPollOption();
      setLatestOptionId(nextOption.localId);

      return {
        ...prevState,
        formError: null,
        formNotice: null,
        options: [...prevState.options, nextOption],
      };
    });
  };

  const setPollType = pollType => {
    setPollState(prevState => ({
      ...prevState,
      pollType,
      formError: null,
      formNotice: null,
    }));
  };

  const setTitle = event => {
    const title = event.target.value;
    setPollState(prevState => ({
      ...prevState,
      title,
      formNotice: null,
      titleError:
        title.length > MAX_POLL_TITLE_LENGTH
          ? `Title must be ${MAX_POLL_TITLE_LENGTH} characters or fewer.`
          : null,
    }));
  };

  const setDescription = event => {
    const description = event.target.value;
    setPollState(prevState => ({
      ...prevState,
      description,
      formError: null,
      formNotice: null,
    }));
  };

  const addOptionMessage = pollState.formError || pollState.formNotice;

  return (
    <div className={css.createPollForm}>
      <input
        id="poll-title"
        className={css.pollTitleInput}
        type="text"
        value={pollState.title || ''}
        disabled={disabled}
        maxLength={MAX_POLL_TITLE_LENGTH + 1}
        placeholder="Title (optional)"
        aria-invalid={Boolean(pollState.titleError)}
        aria-describedby={pollState.titleError ? 'poll-title-error' : undefined}
        onChange={setTitle}
      />
      {pollState.titleError ? (
        <div id="poll-title-error" className={css.pollFieldError} role="alert">
          {pollState.titleError}
        </div>
      ) : null}
      <textarea
        id="poll-description"
        className={css.pollDescriptionInput}
        value={pollState.description || ''}
        disabled={disabled}
        placeholder="Write something..."
        onChange={setDescription}
      />

      <h3 className={css.pollSectionHeading}>Create poll</h3>

      <div className={css.pollOptionsList}>
        {pollState.options.map((option, index) => (
          <PollOptionEditor
            key={option.localId}
            option={option}
            optionNumber={index + 1}
            canRemove={pollState.options.length > MIN_POLL_OPTIONS}
            disabled={disabled}
            autoFocus={latestOptionId === option.localId}
            onChange={setOptionText}
            onSave={saveOption}
            onEdit={editOption}
            onRemove={removeOption}
          />
        ))}
      </div>

      <div className={css.pollControls}>
        <PollTypeSelector value={pollState.pollType} disabled={disabled} onChange={setPollType} />
        <button
          type="button"
          className={css.addOptionButton}
          disabled={disabled}
          onClick={addOption}
          aria-describedby={addOptionMessage ? 'poll-form-message' : undefined}
        >
          <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M13.5 0C6.04432 0 0 6.04432 0 13.5C0 20.9557 6.04432 27 13.5 27C20.9557 27 27 20.9557 27 13.5C27 6.04432 20.9557 0 13.5 0ZM14.7273 18.4091C14.7273 18.7346 14.598 19.0467 14.3678 19.2769C14.1377 19.5071 13.8255 19.6364 13.5 19.6364C13.1745 19.6364 12.8623 19.5071 12.6322 19.2769C12.402 19.0467 12.2727 18.7346 12.2727 18.4091V14.7273H8.59091C8.26542 14.7273 7.95325 14.598 7.7231 14.3678C7.49294 14.1377 7.36364 13.8255 7.36364 13.5C7.36364 13.1745 7.49294 12.8623 7.7231 12.6322C7.95325 12.402 8.26542 12.2727 8.59091 12.2727H12.2727V8.59091C12.2727 8.26542 12.402 7.95326 12.6322 7.7231C12.8623 7.49294 13.1745 7.36364 13.5 7.36364C13.8255 7.36364 14.1377 7.49294 14.3678 7.7231C14.598 7.95326 14.7273 8.26542 14.7273 8.59091V12.2727H18.4091C18.7346 12.2727 19.0467 12.402 19.2769 12.6322C19.5071 12.8623 19.6364 13.1745 19.6364 13.5C19.6364 13.8255 19.5071 14.1377 19.2769 14.3678C19.0467 14.598 18.7346 14.7273 18.4091 14.7273H14.7273V18.4091Z" fill="#2F0F48" />
          </svg>

          Add Option
        </button>
      </div>

      {addOptionMessage ? (
        <div
          id="poll-form-message"
          className={css.pollFieldError}
          role={pollState.formError ? 'alert' : 'status'}
        >
          {addOptionMessage}
        </div>
      ) : null}
    </div>
  );
};

export default CreatePollForm;
